import React from "react";
import { useIntl } from "react-intl";
import messages from "./messages";
import { useTheme, Box, Paper, CircularProgress, IconButton } from "@mui/material";
import { tokens } from "../../../assets/theme";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import Checkbox from "../../../components/controls/Checkbox";
import { svSE, enUS } from '@mui/x-data-grid/locales';

export default function StopReasonCodesDataGrid(props) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const apiRef = useGridApiRef();
    const { formatMessage } = useIntl();
    const language = localStorage.getItem('language') || 'en';
    const translations = language === "sv" ? svSE : enUS;

    const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({});

    let columns = [
      { 
        field: "code", 
        headerName: formatMessage(messages.codeColumnLabel),
        flex: 0.3,
        minWidth: 100,
      },
      { 
        field: "name", 
        headerName: formatMessage(messages.nameColumnLabel),
        flex: 1,
        minWidth: 200
      },
      { 
        field: "colorCode", 
        headerName: formatMessage(messages.colorColumnLabel),
        flex: 0.3,
        minWidth: 100,
        renderCell: (params) => (
          <Box
            sx={{
              width: "20px",
              height: "20px",
              backgroundColor: params.value,
              borderRadius: 1
            }}
          />
        ),
      },
      { 
        field: "stopReasonCodeGroupName", 
        headerName: formatMessage(messages.stopReasonCodeGroupColumnLabel),
        minWidth: 200,
        flex: 1,
        valueGetter: (params) => params.row.stopReasonCodeGroup ? params.row.stopReasonCodeGroup.name : ""
      },
      { 
        field: "sortOrder", 
        headerName: formatMessage(messages.sortOrderColumnLabel),
        minWidth: 100,
        maxWidth: 150,
        flex: 0.5,
      },
      { 
        field: "type", 
        headerName: formatMessage(messages.typeColumnLabel),
        minWidth: 150,
        flex: 0.5,
      },
      { 
          field: "noOEECalculationBool", 
          headerName: formatMessage(messages.noOeeCalculationColumnLabel),
          description: formatMessage(messages.noOeeCalculationColumnDescription),
          minWidth: 200,
          flex: 0.3,
          align: "center",
          renderCell: (params) => (

            <> 
            { 
            
            <Checkbox
              id="noOeeCalculation"
              name="noOeeCalculation"
              label=""
              value={params.value}
              disabled={true}
              onChange={() => {}}
              
              />
   
            }
          </>
        
          ),
      },
      { 
        field: "showMachinePageStatisticsBool", 
        headerName: formatMessage(messages.showMachinePageStatisticsColumnLabel),
        description: formatMessage(messages.showMachinePageStatisticsDescription),
        flex: 0.4,
        minWidth: 200,
        align: "center",
        renderCell: (params) => (
          <> 
            {
            
            <Checkbox
              id="showMachinePageStatisticsBool"
              name="showMachinePageStatisticsBool"
              label=""
              value={params.value}
              disabled={true}
              onChange={() => {}}
              />
   
            }
          </>
        ),
      },
      { 
        field: "canBeUsedForAutocoding", 
        headerName: formatMessage(messages.canBeUsedForAutoCodeLabel),
        description: formatMessage(messages.canBeUsedForAutoCodeDescription),
        flex: 0.4,
        minWidth: 200,
        align: "center",
        renderCell: (params) => (
          <> 
            <Checkbox
              id="canBeUsedForAutocoding"
              name="canBeUsedForAutocoding"
              label=""
              value={params.value}
              disabled={true}
              onChange={() => {}}
              />
          </>
        ),
      },
      {
        field:"edit", 
        headerName:"",
        renderCell:(params) => {
            return(
                <>
                <IconButton
                    aria-label="Edit stop reason code"
                    size="small"
                    sx={{
                        "&:hover": {
                        backgroundColor: colors.primary[500],
                        },
                        backgroundColor: colors.primary[700],
                    }}
                    onClick={() => {
                        props.handleEditStopReasonCodeClick(params.row.id)
                    }}
                    >
                        <RemoveRedEyeOutlinedIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                    aria-label="Delete stop reason code"
                    size="small"
                    sx={{
                        "&:hover": {
                        backgroundColor: colors.primary[500],
                        },
                        backgroundColor: colors.primary[700],
                        marginLeft: "10px"
                    }}
                    onClick={() => {
                      props.handleDeleteStopReasonCodeClick(params.row.id)
                    }}
                    >
                        <DeleteIcon fontSize="inherit" />
                </IconButton>
                </>
            )
        },
        width: 90,
      },
    ];
  
    const columnOrderLocalStorageName = 'stopreson-codes-columnOrder';
    const columnWidthsLocalStorageName = 'stopreson-codes-columnWidths';
    
    // Load persisted column order, widths, and sort order on mount
    function initColumnsFromLocalStorage(columns) {
      let columnsToFix = columns;
      // Load column order
      const savedColumnOrder = localStorage.getItem(columnOrderLocalStorageName);
      if (savedColumnOrder) {
        const orderArray = JSON.parse(savedColumnOrder);
        columnsToFix = reorderColumns(orderArray, columnsToFix);
      }

      // Load column widths
      const savedWidths = localStorage.getItem(columnWidthsLocalStorageName);
      if (savedWidths) {
          const widthMap = JSON.parse(savedWidths);
          columnsToFix = applyColumnWidths(widthMap, columnsToFix);
        }
      return columnsToFix;
    };


    // Reorder columns based on saved order
    const reorderColumns = (orderArray, columnsToFix) => {
      const reorderedColumns = orderArray.map((field) =>
          columnsToFix.find((col) => col.field === field)
      );
      reorderedColumns.push(...columnsToFix.filter((col) => !reorderedColumns.includes(col)));
      return reorderedColumns;
    };

    // Apply saved column widths
    const applyColumnWidths = (widthMap, columnsToFix) => {
      const updatedColumns = columnsToFix.map((col) => ({
        ...col,
        width: widthMap[col.field] || col.width, 
        flex: widthMap[col.field] !== undefined ? undefined : col.flex, 
      }));
      return updatedColumns;
    };

    // Handle column order change
    const handleColumnOrderChange = () => {
      if (!apiRef.current) {
        return;
      }
      const orderedColumns = apiRef.current.getVisibleColumns();
      const newColumnOrder = orderedColumns.map((col) => col.field);
      localStorage.setItem(columnOrderLocalStorageName, JSON.stringify(newColumnOrder));
    };

    // Handle column resize
    const handleColumnResize = (params) => {
      const { colDef, width } = params;
      const newWidths = JSON.parse(localStorage.getItem(columnWidthsLocalStorageName)) || {};
      newWidths[colDef.field] = width;
      localStorage.setItem(columnWidthsLocalStorageName, JSON.stringify(newWidths));
    };

    if(props.isLoading) {
        return <Box padding={2}>
          <Paper
            sx={{
              position: "relative",
              borderRadius: "4px",
            }}
          >
            <CircularProgress color="secondary" />
          </Paper>
        </Box>
     }

     columns = initColumnsFromLocalStorage(columns);

     return (
      <Box padding={2} paddingTop={8}>
        <Paper
          sx={{
            position: "relative",
            borderRadius: "4px",
          }}
        >
          <Box 
            m="0px 0 0 0"
            height="76vh"
            sx={{
                "& .MuiDataGrid-root": {
                    backgroundColor: colors.primary[400],
                    borderColor: `${colors.primary[700]} !important`,
                    borderRadius: "0px",
                    fontSize: "1.0rem",
                    border: "0 !important",
                },
                "& .MuiDataGrid-main": {
                    borderRadius: "0px",
                    border: "0 !important",
                    borderColor: `${colors.primary[700]} !important`,
                },
                "& .MuiDataGrid-cell": {
                    borderColor: `${colors.primary[700]} !important`,
                    borderWidth: "2px !important",
                },
                "& .name.column--cell": {
                    color: colors.greenAccent[300],
                },
                "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: colors.primary[400],
                    fontSize: '1.0rem',
                    borderColor: `${colors.primary[700]} !important`,
                    borderWidth: "2px"
                },
                "& .MuiDataGrid-virtualScroller": {
                    backgroundColor: colors.primary[400],
                },
                "& .MuiDataGrid-footerContainer": {
                    borderTop: "none",
                    backgroundColor: colors.primary[600],
                },
                "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                    color: `${colors.gray[100]} !important`,
                },
                "& .super-app-theme--11": {
                    bgcolor: `${colors.redAccent[500]} !important`,
                },
                "& .super-app-theme--undefined-stop": {
                    bgcolor: `${colors.redAccent[500]} !important`,
                },
                "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
                    opacity: 0,
                    transition: "opacity 0.2s"
                  },
                  
                "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
                    opacity: 1
                },
              }}
          >
            { (!props.stopReasonCodes.isLoading) && <DataGridPro
              apiRef={apiRef}
              localeText={translations.components.MuiDataGrid.defaultProps.localeText}
              onColumnResize={handleColumnResize}
              onColumnOrderChange={handleColumnOrderChange}
              rows={props.stopReasonCodes.data}
              getRowId={ (row) => row.id }
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) =>
                setColumnVisibilityModel(newModel)
              }
              slotProps={{
                  panel: {
                  sx: {
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                          backgroundColor: "#799c4a",
                          opacity: 0.3,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": {
                          backgroundColor: "#799c4a",
                      },
                      "& .MuiDataGrid-panelFooter .MuiButtonBase-root": {
                          color: colors.primary[200],
                          fontSize: "0.75rem",
                          "&:hover": {
                              color: colors.primary[300],
                          },
                      },
                      "& .MuiFormLabel-root.Mui-focused": {
                          color: colors.primary[200],
                      },
                  },
                  }
              }}
            /> }
            { (props.stopReasonCodes.isLoading) && <CircularProgress color="secondary" />}
          </Box>
        </Paper>
      </Box>
    );
}